import React, { useEffect, useState } from 'react';
import { useBusinessInfo, setBusinessInfo, useBookingFormQuery } from '~/lib/Queries';
import useBusinessPostApi from "~/lib/useBusinessPostApi";
import OptionToggle from "~/components/OptionToggle";
import Modal from "~/components/Modal";
import * as Toast from "~/lib/Toast";
import { ArrowUpRightIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import Tooltip from '~/components/Tooltip';
import Loading from '~/components/Loading';
import { getBusinessUrl } from '~/lib/useBusinessNavigate.js';
import { Link } from 'react-router-dom';
import SectionPicker from '~/components/SectionPicker.jsx';
import { ShareEmbed, ShareLink } from '~/components/forms/Share.jsx';

const SuccessPageContent = ({}) => {
    const [shareSection, setShareSection] = useState('share');

    const { data : formDetails } = useBookingFormQuery();

    const shareUrl = formDetails?.url;
    const embedPrefix = formDetails?.embedPrefix;

    return (
        <div className="space-y-4">
            <h2 className="text-lg font-semibold">
                🎉  You’re Live!
            </h2>
            <p className="">
                You’re out of test mode. Clients will now start receiving emails and text messages for booking.
            </p>
            <p className="">
                Next, share your scheduling link or embed into your website to allow clients to start booking!
            </p>

            {!!shareUrl && (
                <div>
                    <SectionPicker
                        value={shareSection}
                        onChange={setShareSection}
                        options={[
                            { value: 'share', label: 'Share Link' },
                            { value: 'embed', label: 'Embed In Webpage' },
                        ]}
                        slim={true}
                        className="flex mb-4"
                    />
                    {shareSection === 'share' && (
                        <ShareLink url={shareUrl} hideLabel={true} />
                    )}
                    {shareSection === 'embed' && (
                        <ShareEmbed url={shareUrl} embedPrefix={embedPrefix} hideLabel={true} />
                    )}
                </div>
            )}
        </div>
    )
}


const TestModeToggle = ({ className = "", setIsVisible }) => {
    const { data: businessData } = useBusinessInfo();
    const { business } = businessData || {};

    const [isTestMode, setIsTestMode] = useState(!!business?.test_mode);
    const [showDisableModal, setShowDisableModal] = useState(false);
    const [justDisabled, setJustDisabled] = useState(false);
    const [checkResults, setCheckResults] = useState(null);
    const [isChecking, setIsChecking] = useState(false);
    const [hasIssues, setHasIssues] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);

    const { save: saveTestModeRequest, isSaving: isSavingTestMode } = useBusinessPostApi('/test-mode');
    const { save: checkTestModeRequest, isSaving: isCheckingTestMode } = useBusinessPostApi('/test-mode/check');

    useEffect(() => {
        if (business) {
            setIsTestMode(!!business.test_mode);
        }
    }, [JSON.stringify(business)]);

    function handleToggle(nextValue) {
        if (!nextValue) {
            openDisableModal();
        } else {
            saveTestMode(true);
        }
    }

    function openDisableModal() {
        setShowDisableModal(true);
        setShowSuccess(false);
        setHasIssues(false);
        setCheckResults(null);

        if (!business) return;

        setIsChecking(true);
        checkTestModeRequest({})
            .then(result => {
                setCheckResults(result);
                const issues = Array.isArray(result?.issues) ? result.issues : [];
                setHasIssues(issues.length > 0);
            })
            .catch(() => {
                // If check fails for any reason, allow user to proceed
                setHasIssues(false);
            })
            .finally(() => {
                setIsChecking(false);
            });
    }

    function saveTestMode(enabled) {
        if (!business) return;

        saveTestModeRequest({
            enabled,
        }).then(result => {
            Toast.success(enabled ? 'Test Mode enabled' : 'Test Mode disabled');

            setJustDisabled(true);
            setBusinessInfo(result);
            setIsTestMode(!!result?.business?.test_mode);

            if (!enabled) {
                // Stay in modal and show success state
                setShowSuccess(true);
            } else {
                setShowDisableModal(false);
            }
        });
    }

    const isVisible = !(!business || (!business.test_mode && !justDisabled));

    useEffect(() => {
        if (setIsVisible) setIsVisible(isVisible);
    }, [isVisible]);

    if (!isVisible || !businessData?.permissions?.isFullAccess) {
        return null;
    }

    const issues = Array.isArray(checkResults?.issues) ? checkResults.issues : [];

    return (
        <>
            <div className={`rounded-xl border p-3 flex items-center ${className}`}>
                <OptionToggle
                    value={isTestMode}
                    onChange={handleToggle}
                    toggleFirst={false}
                    className="mr-2"
                />
                <div className="flex-1 flex gap-2 items-center justify-between">
                    <div className="leading-none">
                        <div className="text-sm font-semibold leading-none">
                            Test Mode
                        </div>
                        {!!businessData?.bookingUrl && (
                            <a href={`${businessData?.bookingUrl}?test=true`} target="_blank" className="text-xs no-underline leading-none">Make a test booking</a>
                        )}
                    </div>
                    <Tooltip message="Test Mode disables sending of emails and texts,<br/>so your clients will not receive any notifications.">
                        <InformationCircleIcon className="w-5 h-5 text-primary-700" />
                    </Tooltip>
                </div>
            </div>

            <Modal isOpen={showDisableModal} onClose={() => setShowDisableModal(false)}>
                {showSuccess ? (
                    <SuccessPageContent />
                ) : (
                    <div className="space-y-4">
                        <h2 className="text-lg font-semibold">
                            Are you sure you want to go live?
                        </h2>
                        {isChecking && (
                            <p className="text-gray-500">
                                <Loading message="Checking your setup before going live..." />
                            </p>
                        )}
                        {hasIssues && (
                            <div className="">
                                <p className="mb-1">A few things to review before going live:</p>
                                <ul className="list-disc marker:text-red-500">
                                    {issues.map((issue, index) => (
                                        <li key={index}>
                                            {issue.message}{' '}
                                            <Link to={getBusinessUrl(issue.url)} className="no-underline">
                                                {issue.linkText || 'Open'} <ArrowUpRightIcon className="w-4 h-4 inline" />
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                                <p className="mt-2">You can still go live now and fix these later.</p>
                            </div>
                        )}
                        {!hasIssues && !isChecking && (
                            <div>
                                Everything looks good with your setup!
                            </div>
                        )}
                        <div className="flex gap-2 mt-4">
                            <button
                                type="button"
                                className="btn-save"
                                onClick={() => saveTestMode(false)}
                                disabled={isSavingTestMode}
                            >
                                {isSavingTestMode ? 'Going Live...' : 'Go Live'}
                            </button>
                            <button
                                type="button"
                                className="btn-gray-pill-lg"
                                onClick={() => setShowDisableModal(false)}
                                disabled={isSavingTestMode}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </Modal>
        </>
    );
};

export default TestModeToggle;

